import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Target, Star, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SIDE = { 1: 'Home', X: 'Draw', 2: 'Away' };

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="rounded-xl border border-[#1e2a45] bg-[#0d1424] p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500"><Icon className="h-4 w-4 text-[#3B82F6]" />{label}</div>
    <div className="mt-2 text-3xl font-bold text-white tabular-nums">{value}</div>
    <div className="mt-1 text-xs text-gray-500">{sub}</div>
  </div>
);

const HitIcon = ({ hit }) => (hit
  ? <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-label="hit" />
  : <XCircle className="h-4 w-4 text-gray-600" aria-label="miss" />);

const RecentRow = ({ r }) => (
  <li className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-[#1e2a45] last:border-0" data-testid={`track-record-row-${r.id}`}>
    <div className="min-w-0">
      <div className="text-sm text-white truncate">{r.home} vs {r.away}</div>
      <div className="text-xs text-gray-500">{r.league} · {new Date(r.kickoff_utc).toLocaleDateString()}</div>
    </div>
    <div className="flex items-center gap-4 text-xs text-gray-400">
      <span className="flex items-center gap-1.5">Value: {SIDE[r.selection]} <HitIcon hit={r.value_hit} /></span>
      <span className="flex items-center gap-1.5">Favourite: {SIDE[r.favourite] || '—'} <HitIcon hit={r.favourite_hit} /></span>
      <span className="text-gray-500">Result: {SIDE[r.result]}</span>
    </div>
  </li>
);

const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

export const TrackRecord = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/sports/picks/track-record`).then((r) => r.json()).then(setData).catch(() => setData({ graded: 0, recent: [] }));
  }, []);
  if (!data) return null;
  const returnTxt = data.flat_stake_return === null ? '—' : `${data.flat_stake_return > 0 ? '+' : ''}${data.flat_stake_return}%`;

  return (
    <section className="mt-16" data-testid="track-record-section">
      <h2 className="text-base md:text-lg font-semibold text-white mb-1">Track record</h2>
      <p className="text-sm text-gray-400 mb-5">Every pick shown here is graded against the final score once the match ends.</p>
      {data.graded === 0 ? (
        <p className="rounded-xl border border-dashed border-[#1e2a45] p-6 text-sm text-gray-400" data-testid="track-record-empty">
          Grading starts once today's fixtures finish{data.pending ? ` — ${data.pending} picks are waiting for a result` : ''}. Check back after full time.
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat icon={Target} label="Value bet hit rate" value={pct(data.value_hit_rate)} sub={`${data.value_hits} of ${data.graded} flagged picks came in`} testId="track-record-value-rate" />
            <Stat icon={Star} label="Favourite hit rate" value={pct(data.favourite_hit_rate)} sub={`${data.favourite_hits} of ${data.graded} favourites won`} testId="track-record-favourite-rate" />
            <Stat icon={TrendingUp} label="Flat-stake return" value={returnTxt} sub="per 1-unit stake on every value pick" testId="track-record-return" />
          </div>
          <ul className="mt-6 rounded-xl border border-[#1e2a45] bg-[#0d1424] px-5" data-testid="track-record-recent">
            {data.recent.map((r) => <RecentRow key={r.id} r={r} />)}
          </ul>
        </>
      )}
    </section>
  );
};

export default TrackRecord;
